import { Component, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

@Component({
  selector: 'app-drag-and-drop-file-upload',
  templateUrl: './drag-and-drop-file-upload.component.html',
  styleUrl: './drag-and-drop-file-upload.component.scss',
  standalone: false
})
export class DragAndDropFileUploadComponent {
  @Output() fileDropped = new EventEmitter<File>();
  @Output() fileHovered = new EventEmitter<boolean>();
  @Output() invalidFile = new EventEmitter<string>();

  @HostBinding('class.file-over') fileOver: boolean = false;

  @Input() allowedExtensions = ['.xlsx', '.xlsm'];

  isDragging: boolean;

  // ✅ Add dragenter event
  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.fileOver = true;
    this.isDragging = true;
    this.fileHovered.emit(true);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // ✅ Set dropEffect to show copy cursor
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.fileOver = true;
    this.isDragging = true;
    this.fileHovered.emit(true);
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    // ✅ Only set fileOver to false if leaving the main element
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;
    
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      this.fileOver = false;
      this.isDragging = false;
      this.fileHovered.emit(false);
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.fileOver = false;
    this.isDragging = false;
    this.fileHovered.emit(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      if (this.isValidFile(file)) {
        this.fileDropped.emit(file);
      } else {
        this.invalidFile.emit('Please upload a valid file');
      }
    }
  }

  handleFileInput($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.fileOver = false;
    this.isDragging = false;
    this.fileHovered.emit(false);

    const file = $event?.target?.files?.[0];      
    if (this.isValidFile(file)) {
      this.fileDropped.emit(file);
    } else {
      this.invalidFile.emit('Please upload a valid file');
    }
  }

  private isValidFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    const hasValidExtension = this.allowedExtensions.includes(fileExtension);
    
    return hasValidExtension;
  }
}
